import { IonContent, IonPage, IonRefresher, IonRefresherContent, IonSpinner } from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import Header from "src/components/Header";
import SemaforoChip from "src/components/SemaforoSla";
import { fmtFecha } from "src/components/SolicitudCapacitacionCampos";
import SstAPI, { LevantamientoResumen } from "src/services/sst";

/**
 * SST > Acciones correctivas (/sst/acciones-correctivas): levantamientos de
 * condiciones inseguras asignados al usuario. Arriba los que esperan sus
 * acciones (por vencimiento), abajo los ya enviados.
 */
const AccionesCorrectivas: React.FC = () => {
  const history = useHistory();
  const [items, setItems] = useState<LevantamientoResumen[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const cargar = useCallback(() => {
    setError("");
    return SstAPI.listar()
      .then((r) => setItems(r.data.data || []))
      .catch((err) => setError(err?.response?.data?.error || "No se pudieron obtener las acciones correctivas"))
      .finally(() => setCargando(false));
  }, []);

  // Las rutas de App.tsx van en un <Switch> (sin IonRouterOutlet): cada
  // navegacion vuelve a montar la pagina y useIonViewWillEnter no se dispara
  // nunca. Por eso la carga va en el montaje; al volver del detalle, despues
  // de enviar, la lista se recarga sola.
  useEffect(() => {
    cargar();
  }, [cargar]);

  const pendientes = items.filter((l) => l.estado === "PENDIENTE_ACCION" || l.estado === "DEVUELTO");
  const enviadas = items.filter((l) => !pendientes.includes(l));

  const fila = (l: LevantamientoResumen) => (
    <div
      key={l.id}
      onClick={() => history.push(`/sst/acciones-correctivas/${l.id}`)}
      style={{
        cursor: "pointer",
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        padding: "10px 12px",
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
        <strong style={{ fontSize: 14 }}>
          N° {l.id} · {l.anomalia}
        </strong>
        <SemaforoChip semaforo={l.semaforo} />
      </div>
      <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
        {l.condicion === "LUGAR"
          ? `${l.ps_nombre || ""}${l.ps_codigo ? ` (${l.ps_codigo})` : ""}`
          : `Uso de EPIs · ${l.colaborador_nombre || ""}`}
      </div>
      <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>
        {l.estado_label} · vence {fmtFecha(l.fecha_limite)}
      </div>
    </div>
  );

  return (
    <IonPage>
      <Header titulo="Acciones Correctivas" botonIzq={{ icono: arrowBackOutline, url: "/home" }} />
      <IonContent id="sst-acciones-correctivas">
        <IonRefresher slot="fixed" onIonRefresh={(e) => cargar().finally(() => e.detail.complete())}>
          <IonRefresherContent />
        </IonRefresher>
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          <p style={{ fontSize: 13, color: "#666", marginTop: 0 }}>
            Levantamientos de condiciones inseguras asignados a vos. Cargá las acciones correctivas antes de la fecha
            límite.
          </p>
          {cargando ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          ) : error ? (
            <p style={{ color: "#b21f2d" }}>{error}</p>
          ) : !items.length ? (
            <p style={{ color: "#777" }}>No tenés acciones correctivas asignadas.</p>
          ) : (
            <>
              <h6 style={{ color: "var(--garnet, #910434)", fontWeight: 700 }}>Pendientes ({pendientes.length})</h6>
              {pendientes.length ? pendientes.map(fila) : <p style={{ color: "#777", fontSize: 13 }}>Ninguna.</p>}
              {enviadas.length > 0 && (
                <>
                  <h6 style={{ color: "#555", fontWeight: 700, marginTop: 20 }}>Enviadas</h6>
                  {enviadas.map(fila)}
                </>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AccionesCorrectivas;
